import { QueryClient, QueryFunction } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

// Clerk's getToken function — set at runtime by ClerkAuthBridge
let clerkGetToken: (() => Promise<string | null>) | null = null;
let resolveClerkGetTokenReady: (() => void) | null = null;
const clerkGetTokenReady = new Promise<void>((resolve) => {
  resolveClerkGetTokenReady = resolve;
});
const CLERK_TOKEN_READY_ATTEMPTS = 100;
const CLERK_TOKEN_READY_INTERVAL_MS = 50;

/**
 * Register Clerk's getToken function for use by getAuthHeaders.
 * Called once by ClerkAuthBridge component when Clerk is loaded.
 */
export function registerClerkGetToken(fn: () => Promise<string | null>): void {
  clerkGetToken = fn;
  resolveClerkGetTokenReady?.();
  resolveClerkGetTokenReady = null;
}

export class ApiError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let text = 'Something went wrong. Try again, or contact support if it continues.';
    let code: string | undefined;
    try {
      const body = await res.json();
      const message = typeof body?.error === 'string' ? body.error : body?.message;
      if (typeof message === 'string' && message.trim()) text = message.trim();
      if (typeof body?.code === 'string' && /^[A-Z][A-Z0-9_]{0,63}$/.test(body.code)) code = body.code;
    } catch {
      // Proxy HTML and malformed bodies must not become toast text.
    }
    // Keep the status prefix for callers that distinguish conflicts and missing records.
    const message = `${res.status}: ${text}`;
    throw new ApiError(message.length > 240 ? `${message.slice(0, 237)}...` : message, code);
  }
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  // A signed-in protected route can render before ClerkAuthBridge's parent effect
  // registers getToken. Hold the request instead of caching an unauthenticated read.
  if (!clerkGetToken) await clerkGetTokenReady;

  // Clerk can register getToken before the destination tab has hydrated its
  // active session. Never turn that transient null into an unauthenticated API
  // request, because React Query deliberately does not retry failed reads.
  for (let attempt = 0; attempt < CLERK_TOKEN_READY_ATTEMPTS; attempt += 1) {
    const token = await clerkGetToken!();
    if (token) return { 'Authorization': `Bearer ${token}` };

    if (attempt < CLERK_TOKEN_READY_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, CLERK_TOKEN_READY_INTERVAL_MS));
    }
  }

  throw new Error('Clerk session token unavailable after auth readiness');
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(url, {
    method,
    headers: {
      ...authHeaders,
      ...(data ? { "Content-Type": "application/json" } : {}),
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Build URL from queryKey, handling objects as query parameters
    const pathSegments: string[] = [];
    const params = new URLSearchParams();

    for (const segment of queryKey) {
      if (typeof segment === "string" || typeof segment === "number") {
        pathSegments.push(String(segment));
      } else if (typeof segment === "object" && segment !== null) {
        // Convert object to query parameters
        for (const [key, value] of Object.entries(segment)) {
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach((v) => params.append(key, String(v)));
            } else {
              params.append(key, String(value));
            }
          }
        }
      }
    }

    const url = pathSegments.join("/");
    const queryString = params.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    const authHeaders = await getAuthHeaders();
    const res = await fetch(fullUrl, {
      headers: authHeaders,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
      onError: (error: Error) => {
        Sentry.captureException(error, {
          tags: { source: "react-query-mutation" },
        });
      },
    },
  },
});
